import type { BarDatum } from '@nivo/bar';
import type { User } from 'api/models/mainservice';
import type { Codebook } from 'common/models';
import dayjs from 'dayjs';
import { interventionStateName } from 'helpers/orderHelpers';
import { getNow } from 'helpers/timeHelpers';
import type { TFunction } from 'i18next';
import type { InterventionState } from 'pages/interventions/models';
import { InterventionStateArray } from 'pages/interventions/models';

import type { DashboardOrderIntervention } from '../models';

interface Group {
  id: string;
  items: DashboardOrderIntervention[];
}

interface AggregateItem {
  id: string;
  label: string;
  value: number;
}

const groupByDays = (interventions: DashboardOrderIntervention[]) => {
  const data = interventions.reduce<Group[]>((acc, item) => {
    const created = dayjs.utc(item.created).format('D.MM.');
    const day = acc.find((x) => x.id === created);
    if (!day) {
      return [...acc, { id: created, items: [item] }];
    }
    return [...acc.filter((x) => x.id !== day.id), { id: day.id, items: [...day.items, item] }];
  }, []);

  const today = getNow();
  const days: string[] = [];
  for (let i = -7; i < 0; i += 1) {
    days.push(
      dayjs(today)
        .add(1 + i, 'day')
        .format('D.MM.')
    );
  }

  return days.map((day) => {
    const dayData = data.find((x) => x.id === day);
    if (!dayData) {
      return { id: day, items: [] };
    }
    return dayData;
  });
};

const groupByMonths = (interventions: DashboardOrderIntervention[]) => {
  const data = interventions.reduce<Group[]>((acc, item) => {
    const created = dayjs.utc(item.created).format('MMMM');
    const month = acc.find((x) => x.id === created);
    if (!month) {
      return [...acc, { id: created, items: [item] }];
    }
    return [
      ...acc.filter((x) => x.id !== month.id),
      { id: month.id, items: [...month.items, item] }
    ];
  }, []);

  const today = getNow();
  const months: string[] = [];
  for (let i = -5; i < 0; i += 1) {
    months.push(
      dayjs(today)
        .add(1 + i, 'month')
        .format('MMMM')
    );
  }

  return months.map((month) => {
    const monthData = data.find((x) => x.id === month);
    if (!monthData) {
      return { id: month, items: [] };
    }
    return monthData;
  });
};

const aggregateAssignees = (interventions: DashboardOrderIntervention[]) => {
  return interventions.reduce<AggregateItem[]>((acc, item) => {
    const assignee = acc.find((x) => x.id === item.primaryAssigneeId);
    if (!assignee) {
      return [...acc, { id: item.primaryAssigneeId, label: item.primaryAssigneeId, value: 1 }];
    }
    return [...acc.filter((x) => x.id !== assignee.id), { ...assignee, value: assignee.value + 1 }];
  }, []);
};

const getAssigneeKeys = (interventions: DashboardOrderIntervention[]) => {
  return interventions.reduce<string[]>((acc, item) => {
    if (!acc.find((x) => x === item.primaryAssigneeId)) {
      return [...acc, item.primaryAssigneeId];
    }
    return acc;
  }, []);
};

const aggregateCategories = (interventions: DashboardOrderIntervention[]) => {
  return interventions.reduce<AggregateItem[]>((acc, item) => {
    const category = acc.find((x) => x.id === item.categoryId);
    if (!category) {
      return [...acc, { id: item.categoryId, label: item.categoryId, value: 1 }];
    }
    return [...acc.filter((x) => x.id !== category.id), { ...category, value: category.value + 1 }];
  }, []);
};

const getCategoryKeys = (interventions: DashboardOrderIntervention[]) => {
  return interventions.reduce<string[]>((acc, item) => {
    if (!acc.find((x) => x === item.categoryId)) {
      return [...acc, item.categoryId];
    }
    return acc;
  }, []);
};

const aggregateStates = (interventions: DashboardOrderIntervention[]) => {
  return interventions.reduce<AggregateItem[]>((acc, item) => {
    const state = acc.find((x) => x.id === item.state);
    if (!state) {
      return [...acc, { id: item.state, label: item.state, value: 1 }];
    }
    return [...acc.filter((x) => x.id !== state.id), { ...state, value: state.value + 1 }];
  }, []);
};

const getStateKeys = () => {
  return InterventionStateArray;
};

const translateLabel = (id: string, labels: Codebook[] | { id: string; name: string }[]) => {
  return labels.find((xx) => xx.id === id)?.name || id;
};

export const prepareData = (
  interventions: DashboardOrderIntervention[],
  t: TFunction,
  selectedPeriod: 'days' | 'months',
  selectedType: 'state' | 'category' | 'assignee',
  users: User[],
  orderCategories: Codebook[]
) => {
  const usersCodebook = users.map((x) => ({ id: x.id, name: `${x.firstName} ${x.lastName}` }));

  let grouped: Group[] = [];
  if (selectedPeriod === 'days') grouped = groupByDays(interventions);
  else if (selectedPeriod === 'months') grouped = groupByMonths(interventions);

  let data: { id: string; items: AggregateItem[] }[] = [];
  let keys: string[] = [];
  if (selectedType === 'assignee') {
    data = grouped.map((group) => {
      const items = aggregateAssignees(group.items).map((x) => ({
        ...x,
        label: translateLabel(x.label, usersCodebook)
      }));
      return { id: group.id, items };
    });
    keys = getAssigneeKeys(interventions).map((x) => translateLabel(x, usersCodebook));
  } else if (selectedType === 'category') {
    data = grouped.map((group) => {
      const items = aggregateCategories(group.items).map((x) => ({
        ...x,
        label: translateLabel(x.label, orderCategories)
      }));
      return { id: group.id, items };
    });
    keys = getCategoryKeys(interventions).map((x) => translateLabel(x, orderCategories));
  } else if (selectedType === 'state') {
    data = grouped.map((group) => {
      const items = aggregateStates(group.items).map((x) => ({
        ...x,
        label: t(interventionStateName(x.label as InterventionState))
      }));
      return { id: group.id, items };
    });
    keys = getStateKeys().map((x) => t(interventionStateName(x as InterventionState)));
  }

  // Transform for Bar Chart
  const items = data.map((item) => {
    return item.items.reduce<BarDatum>((acc, x) => ({ ...acc, [x.label]: x.value }), {
      id: item.id,
      total: item.items.reduce((acc, x) => acc + x.value, 0)
    });
  });

  return {
    keys,
    items
  };
};
